import React from "react";
import { hierarchy, treemap, treemapSquarify } from "d3-hierarchy";

/**
 * Cyber visualisations.
 *
 * Rendered as React-owned SVG rather than through d3-selection, so there is one
 * thing mutating the DOM instead of two fighting over it. d3 is used only for the
 * squarified treemap layout, which is real arithmetic worth importing.
 *
 * Every chart here is meant to change a decision. A pie chart of severity counts
 * would look fine and tell nobody anything they could act on, so there isn't one.
 */

const BAND_COLOUR = {
  CRITICAL: "#dc2626", HIGH: "#ea580c", MODERATE: "#d97706", LOW: "#059669",
  NONE: "#94a3b8",
};
const SEV_COLOUR = {
  CRITICAL: "#dc2626", HIGH: "#ea580c", MEDIUM: "#d97706", LOW: "#0284c7",
  NONE: "#94a3b8",
};

/** Crisp charts need real pixel widths — a scaled viewBox blurs the labels. */
export function useMeasure() {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState(720);
  React.useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect?.width;
      if (!w) return;
      const next = Math.max(320, Math.floor(w));
      // Ignore sub-pixel churn. A chart that reacts to a fractional change can
      // trade single pixels with its container indefinitely.
      setWidth((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function Tip({ x, y, width, children }) {
  if (!children) return null;
  // Flip the tooltip to the left near the right edge so it never clips out.
  const flip = x > width - 190;
  return (
    <foreignObject x={flip ? x - 196 : x + 12} y={Math.max(0, y - 12)}
                   width={190} height={132} style={{ pointerEvents: "none" }}>
      <div style={{ background: "rgba(15,23,42,.95)", color: "var(--cares-text-max)", padding: "7px 9px",
                    borderRadius: 8, fontSize: 10.5, lineHeight: 1.45,
                    boxShadow: "0 6px 18px rgba(0,0,0,.28)" }}>
        {children}
      </div>
    </foreignObject>
  );
}

const Title = ({ children, hint }) => (
  <div style={{ marginBottom: 8 }}>
    <h3 style={{ fontSize: 13, fontWeight: 700, color: "var(--cares-text-1)", margin: 0 }}>{children}</h3>
    {hint && <p style={{ fontSize: 11, color: "var(--cares-text-note)", margin: "3px 0 0" }}>{hint}</p>}
  </div>
);

/* ==================================================== exploitation risk matrix */
/**
 * The chart that changes what you patch first.
 *
 * Severity alone ranks a theoretical 9.8 above a 7.2 that is being exploited
 * today. Plotting CVSS against EPSS — the probability of exploitation in the next
 * thirty days — separates "bad if someone bothers" from "someone is bothering",
 * and sizing by device count adds what it would cost you.
 */
export function RiskMatrix({ vulnerabilities }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);
  const H = 340, PAD = { t: 14, r: 16, b: 40, l: 46 };
  const iw = Math.max(200, width - PAD.l - PAD.r), ih = H - PAD.t - PAD.b;

  const x = (epss) => PAD.l + Math.min(1, Math.max(0, epss)) * iw;
  const y = (cvss) => PAD.t + ih - (Math.min(10, Math.max(0, cvss)) / 10) * ih;
  const maxDevices = Math.max(...vulnerabilities.map((v) => v.devices || 0), 1);
  const r = (d) => 3.5 + Math.sqrt((d || 0) / maxDevices) * 13;

  // Quadrant thresholds: CVSS 7 is the high/critical boundary, EPSS 0.5 is where
  // exploitation stops being hypothetical.
  const xMid = x(0.5), yMid = y(7);

  const sorted = [...vulnerabilities].sort((a, b) => (a.devices || 0) - (b.devices || 0));

  return (
    <div ref={ref}>
      <Title hint="Severity against probability of exploitation. Bubble size is the number of devices behind the finding.">
        Exploitation risk matrix
      </Title>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}>
        <rect x={xMid} y={PAD.t} width={PAD.l + iw - xMid} height={yMid - PAD.t}
              fill="color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" />
        <text x={PAD.l + iw - 6} y={PAD.t + 13} textAnchor="end"
              style={{ fontSize: 10, fontWeight: 700, fill: "var(--cares-orange)" }}>
          FIX FIRST — severe and likely
        </text>
        <text x={PAD.l + 6} y={PAD.t + 13}
              style={{ fontSize: 10, fill: "var(--cares-surface-raised)" }}>severe, unlikely</text>
        <text x={PAD.l + iw - 6} y={PAD.t + ih - 6} textAnchor="end"
              style={{ fontSize: 10, fill: "var(--cares-surface-raised)" }}>likely, lower impact</text>

        {[0, 2, 4, 6, 8, 10].map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={PAD.l + iw} y1={y(v)} y2={y(v)} stroke="var(--cares-line)" />
            <text x={PAD.l - 7} y={y(v) + 3} textAnchor="end"
                  style={{ fontSize: 9.5, fill: "var(--cares-surface-raised)" }}>{v}</text>
          </g>
        ))}
        {[0, 0.25, 0.5, 0.75, 1].map((v) => (
          <text key={v} x={x(v)} y={PAD.t + ih + 15} textAnchor="middle"
                style={{ fontSize: 9.5, fill: "var(--cares-surface-raised)" }}>{v * 100}%</text>
        ))}
        <line x1={xMid} x2={xMid} y1={PAD.t} y2={PAD.t + ih}
              stroke="var(--cares-line-strong)" strokeDasharray="3 3" />
        <line x1={PAD.l} x2={PAD.l + iw} y1={yMid} y2={yMid}
              stroke="var(--cares-line-strong)" strokeDasharray="3 3" />

        {sorted.map((v) => {
          const on = hover?.cveId === v.cveId;
          return (
            <circle key={v.cveId + v.productId} cx={x(v.epss)} cy={y(v.cvss)} r={r(v.devices)}
                    fill={v.knownExploited ? "var(--cares-orange)" : SEV_COLOUR[v.severity]}
                    fillOpacity={v.knownExploited ? 0.75 : 0.3}
                    stroke={v.knownExploited ? "var(--cares-orange)" : SEV_COLOUR[v.severity]}
                    strokeWidth={on ? 2.5 : v.knownExploited ? 1.5 : 0.8}
                    style={{ cursor: "pointer", transition: "stroke-width .1s" }}
                    onMouseEnter={() => setHover(v)} onMouseLeave={() => setHover(null)} />
          );
        })}

        <text x={PAD.l + iw / 2} y={H - 6} textAnchor="middle"
              style={{ fontSize: 10.5, fill: "var(--cares-surface-raised)", fontWeight: 600 }}>
          EPSS — probability of exploitation within 30 days
        </text>
        <text transform={`translate(12,${PAD.t + ih / 2}) rotate(-90)`} textAnchor="middle"
              style={{ fontSize: 10.5, fill: "var(--cares-surface-raised)", fontWeight: 600 }}>CVSS severity</text>

        {hover && (
          <Tip x={x(hover.epss)} y={y(hover.cvss)} width={width}>
            <div style={{ fontWeight: 700 }}>{hover.cveId}</div>
            <div style={{ color: "var(--cares-text-dim)" }}>{hover.productName}</div>
            <div style={{ marginTop: 4 }}>
              CVSS {hover.cvss} · EPSS {(hover.epss * 100).toFixed(1)}%
            </div>
            <div>{(hover.devices || 0).toLocaleString()} devices</div>
            {hover.knownExploited && (
              <div style={{ color: "var(--cares-orange)", fontWeight: 700, marginTop: 3 }}>
                Actively exploited{hover.kevOverdue
                  ? ` · ${Math.abs(hover.daysUntilKevDue)}d overdue` : ""}
              </div>
            )}
          </Tip>
        )}
      </svg>
      <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 4 }}>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             background: "var(--cares-orange)", marginRight: 4 }} />
          Known exploited</span>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             background: "var(--cares-orange)", opacity: .35, marginRight: 4 }} />
          High</span>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             background: "var(--cares-amber)", opacity: .35, marginRight: 4 }} />
          Medium</span>
      </div>
    </div>
  );
}

/* ================================================================= treemap */
/** Where the exposed estate actually is, by footprint rather than by count. */
export function ExposureTreemap({ products, metric = "devices" }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);
  const H = 320;

  const nodes = React.useMemo(() => {
    const value = (p) => Math.max(1, metric === "spend" ? p.annualSpend : p.devices);
    const root = hierarchy({ children: products.filter((p) => value(p) > 0) })
      .sum(value)
      .sort((a, b) => b.value - a.value);
    treemap().tile(treemapSquarify).size([width, H]).paddingInner(2)(root);
    return root.leaves();
  }, [products, width, metric]);

  return (
    <div ref={ref}>
      <Title hint={metric === "spend"
        ? "Rectangle area is annual spend; colour is exposure band."
        : "Rectangle area is deployed devices; colour is exposure band. Big and red is where an incident would hurt most."}>
        Estate exposure map
      </Title>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}>
        {nodes.map((node) => {
          const p = node.data;
          const w = node.x1 - node.x0, h = node.y1 - node.y0;
          const on = hover?.productId === p.productId;
          return (
            <g key={p.productId}
               onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}
               style={{ cursor: "pointer" }}>
              <rect x={node.x0} y={node.y0} width={w} height={h} rx={3}
                    fill={BAND_COLOUR[p.band]} fillOpacity={on ? 0.95 : 0.72}
                    stroke="var(--cares-line-card)" strokeWidth={on ? 2 : 1} />
              {w > 54 && h > 26 && (
                <text x={node.x0 + 5} y={node.y0 + 13}
                      style={{ fontSize: 9.5, fontWeight: 700, fill: "var(--cares-surface-card)",
                               pointerEvents: "none" }}>
                  {p.product.length * 5.4 > w - 8
                    ? `${p.product.slice(0, Math.floor((w - 8) / 5.4))}…`
                    : p.product}
                </text>
              )}
              {w > 54 && h > 40 && (
                <text x={node.x0 + 5} y={node.y0 + 25}
                      style={{ fontSize: 9, fill: "var(--cares-surface-card)", fillOpacity: .85,
                               pointerEvents: "none" }}>
                  {metric === "spend"
                    ? `$${Math.round(p.annualSpend / 1000)}k`
                    : p.devices.toLocaleString()}
                </text>
              )}
            </g>
          );
        })}
        {hover && (
          <Tip x={width / 2} y={20} width={width}>
            <div style={{ fontWeight: 700 }}>{hover.product}</div>
            <div style={{ color: "var(--cares-text-dim)" }}>{hover.manufacturer}</div>
            <div style={{ marginTop: 4 }}>
              Exposure {hover.exposure} · {hover.band}
            </div>
            <div>{hover.devices.toLocaleString()} devices · ${Math.round(hover.annualSpend).toLocaleString()}</div>
            {hover.counts?.kev > 0 && (
              <div style={{ color: "var(--cares-orange)", fontWeight: 700 }}>
                {hover.counts.kev} actively exploited
              </div>
            )}
          </Tip>
        )}
      </svg>
      <div style={{ display: "flex", gap: 12, fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 6 }}>
        {["CRITICAL", "HIGH", "MODERATE", "LOW"].map((b) => (
          <span key={b}>
            <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2,
                           background: BAND_COLOUR[b], marginRight: 4 }} />{b}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ================================================================ runway */
/**
 * Support expiry and authorisation expiry on one axis.
 *
 * These live in different systems and are never looked at together, which is how
 * a platform ends up losing vendor support in the same quarter its ATO lapses.
 */
export function RunwayTimeline({ products, atos, months = 24 }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);

  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth() + months, today.getDate());
  const start = new Date(today.getFullYear(), today.getMonth() - 6, 1);
  const span = end - start;

  const rows = React.useMemo(() => {
    const out = [];
    products.forEach((p) => {
      if (!p.endOfSupport) return;
      out.push({ key: `p-${p.productId}`, label: p.product, kind: "support",
                 date: new Date(p.endOfSupport), devices: p.devices,
                 detail: `Vendor support ends · ${p.devices.toLocaleString()} devices` });
    });
    (atos || []).forEach((a) => {
      if (!a.expiration_date) return;
      out.push({ key: `a-${a.ato_id}`, label: a.system_name, kind: "ato",
                 date: new Date(a.expiration_date), devices: 0,
                 detail: `Authorisation expires · ${a.fips199} impact` });
    });
    return out.filter((r) => r.date >= start && r.date <= end)
              .sort((a, b) => a.date - b.date);
  }, [products, atos, start, end]);

  // The label gutter shrinks on narrow containers. A fixed 150px gutter plus a
  // 160px minimum plot area needs 324px, which is more than the 320px floor
  // useMeasure allows — so at the narrowest size the chart drew past its own
  // box. Deriving the gutter from the width keeps the drawing inside it.
  const PAD = { t: 26, r: 14, b: 26, l: Math.round(Math.min(150, Math.max(88, width * 0.34))) };
  const rowH = 17;
  const H = PAD.t + PAD.b + Math.max(1, rows.length) * rowH;
  const iw = Math.max(120, width - PAD.l - PAD.r);
  const x = (d) => PAD.l + ((d - start) / span) * iw;

  const ticks = [];
  for (let i = 0; i <= months + 6; i += 3) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    if (d <= end) ticks.push(d);
  }

  return (
    <div ref={ref}>
      <Title hint="Vendor support and authorisation expiry on one axis — the two clocks nobody reads together.">
        Support &amp; authorisation runway
      </Title>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}>
        {ticks.map((d) => (
          <g key={d.toISOString()}>
            <line x1={x(d)} x2={x(d)} y1={PAD.t - 8} y2={H - PAD.b} stroke="var(--cares-line)" />
            <text x={x(d)} y={PAD.t - 12} textAnchor="middle"
                  style={{ fontSize: 9, fill: "var(--cares-surface-raised)" }}>
              {d.toLocaleDateString(undefined, { month: "short", year: "2-digit" })}
            </text>
          </g>
        ))}
        <line x1={x(today)} x2={x(today)} y1={PAD.t - 8} y2={H - PAD.b}
              stroke="var(--cares-line)" strokeWidth="1.5" />
        <text x={x(today)} y={H - PAD.b + 13} textAnchor="middle"
              style={{ fontSize: 9.5, fontWeight: 700, fill: "var(--cares-surface-page)" }}>today</text>

        {rows.map((r, i) => {
          const cy = PAD.t + i * rowH + rowH / 2;
          const past = r.date < today;
          const soon = !past && (r.date - today) / 86400000 <= 180;
          const colour = past ? "#dc2626" : soon ? "#ea580c" : "#64748b";
          const on = hover?.key === r.key;
          return (
            <g key={r.key} onMouseEnter={() => setHover(r)} onMouseLeave={() => setHover(null)}
               style={{ cursor: "pointer" }}>
              <rect x={0} y={cy - rowH / 2} width={width} height={rowH}
                    fill={on ? "var(--cares-surface-board)" : "transparent"} />
              <text x={PAD.l - 8} y={cy + 3} textAnchor="end"
                    style={{ fontSize: 9.5, fill: on ? "var(--cares-text-1)" : "var(--cares-text-3)" }}>
                {r.label.length > 24 ? `${r.label.slice(0, 23)}…` : r.label}
              </text>
              <line x1={x(today)} x2={x(r.date)} y1={cy} y2={cy}
                    stroke={colour} strokeOpacity={.32} strokeWidth={on ? 2.5 : 1.5} />
              {r.kind === "support" ? (
                <rect x={x(r.date) - 4} y={cy - 4} width={8} height={8} rx={1.5}
                      fill={colour} />
              ) : (
                <circle cx={x(r.date)} cy={cy} r={4.2} fill="var(--cares-surface-card)"
                        stroke={colour} strokeWidth="2" />
              )}
            </g>
          );
        })}

        {hover && (
          <Tip x={Math.min(x(hover.date), width - 200)} y={PAD.t} width={width}>
            <div style={{ fontWeight: 700 }}>{hover.label}</div>
            <div style={{ color: "var(--cares-text-dim)", marginTop: 3 }}>{hover.detail}</div>
            <div style={{ marginTop: 4 }}>{hover.date.toISOString().slice(0, 10)}</div>
          </Tip>
        )}
      </svg>
      <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 4 }}>
        <span><span style={{ display: "inline-block", width: 8, height: 8,
                             background: "var(--cares-surface-raised)", marginRight: 5 }} />Vendor support ends</span>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             border: "2px solid var(--cares-line-strong)", marginRight: 5 }} />ATO expires</span>
        <span style={{ color: "var(--cares-orange)" }}>red = already passed</span>
      </div>
    </div>
  );
}

/* =========================================================== blast radius */
/**
 * Which shared components would turn one advisory into an estate-wide incident.
 *
 * A bipartite arc rather than a force-directed graph: the relationship is
 * strictly component-to-product, and a settling physics simulation makes that
 * harder to read, not easier.
 */
export function BlastRadius({ shared }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);

  const components = (shared || []).slice(0, 10);
  const productNames = React.useMemo(() => {
    const set = new Set();
    components.forEach((c) => (c.product_names || []).forEach((p) => set.add(p)));
    return [...set].sort();
  }, [components]);

  const rowH = 19;
  const H = Math.max(components.length, productNames.length) * rowH + 44;
  const leftX = 150, rightX = Math.max(leftX + 120, width - 170);
  const cy = (i, n) => 30 + (H - 54) * ((i + 0.5) / Math.max(1, n));

  const productIndex = Object.fromEntries(productNames.map((p, i) => [p, i]));

  return (
    <div ref={ref}>
      <Title hint="A component used by many products is one advisory away from becoming an estate-wide incident.">
        Shared component blast radius
      </Title>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}>
        <text x={leftX} y={16} textAnchor="end"
              style={{ fontSize: 9.5, fontWeight: 700, fill: "var(--cares-surface-raised)" }}>COMPONENT</text>
        <text x={rightX} y={16}
              style={{ fontSize: 9.5, fontWeight: 700, fill: "var(--cares-surface-raised)" }}>PRODUCT</text>

        {components.map((c, i) => {
          const y1 = cy(i, components.length);
          const on = hover === c.name;
          return (
            <g key={c.name}>
              {(c.product_names || []).map((p) => {
                const y2 = cy(productIndex[p], productNames.length);
                const mid = (leftX + rightX) / 2;
                return (
                  <path key={p}
                        d={`M${leftX + 6},${y1} C${mid},${y1} ${mid},${y2} ${rightX - 6},${y2}`}
                        fill="none"
                        stroke={c.vulnerable ? "var(--cares-orange)" : "var(--cares-purple)"}
                        strokeOpacity={on ? 0.75 : hover ? 0.06 : 0.2}
                        strokeWidth={on ? 1.6 : 1} />
                );
              })}
              <circle cx={leftX + 6} cy={y1} r={on ? 5 : 3.6}
                      fill={c.vulnerable ? "var(--cares-orange)" : "var(--cares-purple)"} />
              <text x={leftX - 4} y={y1 + 3} textAnchor="end"
                    onMouseEnter={() => setHover(c.name)} onMouseLeave={() => setHover(null)}
                    style={{ fontSize: 10, cursor: "pointer",
                             fontWeight: on ? 700 : 500,
                             fill: c.vulnerable ? "var(--cares-orange)" : "var(--cares-text-2)" }}>
                {c.name.length > 22 ? `${c.name.slice(0, 21)}…` : c.name}
                <tspan style={{ fill: "var(--cares-surface-raised)", fontWeight: 400 }}> ×{c.products}</tspan>
              </text>
            </g>
          );
        })}

        {productNames.map((p, i) => {
          const y = cy(i, productNames.length);
          const on = hover && components.find(
            (c) => c.name === hover && (c.product_names || []).includes(p));
          return (
            <g key={p}>
              <circle cx={rightX - 6} cy={y} r={on ? 4.5 : 3} fill={on ? "var(--cares-text-1)" : "var(--cares-text-dim)"} />
              <text x={rightX + 2} y={y + 3}
                    style={{ fontSize: 9.5, fill: on ? "var(--cares-text-1)" : "var(--cares-text-note)",
                             fontWeight: on ? 700 : 400 }}>
                {p.length > 22 ? `${p.slice(0, 21)}…` : p}
              </text>
            </g>
          );
        })}
      </svg>
      <p style={{ fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 4 }}>
        Hover a component to trace it. Red components already carry an advisory.
      </p>
    </div>
  );
}

/* ========================================================= weakness profile */
/** What kinds of problem the estate has, rather than how many. */
export function WeaknessProfile({ vulnerabilities }) {
  // No useMeasure here on purpose. This chart is plain divs, and sizing the bar
  // from a measured width made the row 4px wider than its own container: the
  // label (176) plus two 8px gaps plus the 44px count is 236, but barW only
  // subtracted 232. Inside a flex item with the default min-width:auto the panel
  // cannot shrink below its content, so every ResizeObserver tick grew it another
  // four pixels and the chart crept to the right. Flex sizes the bar correctly
  // and removes the feedback path entirely.
  const rows = React.useMemo(() => {
    const by = {};
    vulnerabilities.forEach((v) => {
      const k = v.cweName || v.cwe || "Unclassified";
      by[k] = by[k] || { name: k, cwe: v.cwe, total: 0, kev: 0, devices: 0 };
      by[k].total += 1;
      by[k].kev += v.knownExploited ? 1 : 0;
      by[k].devices += v.devices || 0;
    });
    return Object.values(by).sort((a, b) => b.total - a.total).slice(0, 9);
  }, [vulnerabilities]);

  const max = Math.max(...rows.map((r) => r.total), 1);

  return (
    <div>
      <Title hint="Recurring weakness classes point at a systemic cause; one-offs point at one vendor.">
        Weakness profile (CWE)
      </Title>
      <div>
        {rows.map((r) => (
          <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 8,
                                     marginBottom: 6 }}>
            <div style={{ flex: "0 0 176px", fontSize: 11, color: "var(--cares-text-2)", textAlign: "right",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                 title={`${r.cwe} — ${r.name}`}>
              {r.name}
            </div>
            {/* minWidth:0 lets this shrink; without it a flex item refuses to go
                below its content and pushes the whole panel wider. */}
            <div style={{ flex: "1 1 auto", minWidth: 0, height: 15, background: "var(--cares-surface-board)",
                          borderRadius: 3, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, width: `${(r.total / max) * 100}%`,
                            background: "color-mix(in srgb, var(--cares-purple) 22%, var(--cares-surface-card))", borderRadius: 3 }} />
              {r.kev > 0 && (
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0,
                              width: `${(r.kev / max) * 100}%`, background: "var(--cares-orange)",
                              borderRadius: 3 }} />
              )}
            </div>
            <div style={{ flex: "0 0 44px", fontSize: 10.5, color: "var(--cares-text-label)" }}>
              {r.total}{r.kev > 0 && <span style={{ color: "var(--cares-orange)", fontWeight: 700 }}>
                {" "}({r.kev})</span>}
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 6 }}>
        Bar length is finding count; the red segment is the portion already being
        exploited.
      </p>
    </div>
  );
}

/* ============================================================ trend chart */
/** Multi-series posture over time, with axes — the sparklines had no scale. */
export function TrendChart({ series, fields }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);
  const H = 260, PAD = { t: 14, r: 14, b: 28, l: 40 };
  const iw = Math.max(160, width - PAD.l - PAD.r), ih = H - PAD.t - PAD.b;

  const all = fields.flatMap((f) => series.map((s) => Number(s[f.key] || 0)));
  const max = Math.max(...all, 1), min = Math.min(...all, 0);
  const span = max - min || 1;
  const x = (i) => PAD.l + (i / Math.max(1, series.length - 1)) * iw;
  const y = (v) => PAD.t + ih - ((v - min) / span) * ih;

  const idx = hover != null
    ? Math.min(series.length - 1, Math.max(0, Math.round(((hover - PAD.l) / iw) * (series.length - 1))))
    : null;

  return (
    <div ref={ref}>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}
           onMouseMove={(e) => {
             const box = e.currentTarget.getBoundingClientRect();
             setHover(e.clientX - box.left);
           }}
           onMouseLeave={() => setHover(null)}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const v = min + span * f;
          return (
            <g key={f}>
              <line x1={PAD.l} x2={PAD.l + iw} y1={y(v)} y2={y(v)} stroke="var(--cares-line)" />
              <text x={PAD.l - 6} y={y(v) + 3} textAnchor="end"
                    style={{ fontSize: 9, fill: "var(--cares-surface-raised)" }}>{Math.round(v)}</text>
            </g>
          );
        })}

        {fields.map((f) => (
          <polyline key={f.key} fill="none" stroke={f.colour} strokeWidth="1.6"
                    points={series.map((s, i) => `${x(i)},${y(Number(s[f.key] || 0))}`).join(" ")} />
        ))}

        {idx != null && (
          <>
            <line x1={x(idx)} x2={x(idx)} y1={PAD.t} y2={PAD.t + ih}
                  stroke="var(--cares-line-strong)" strokeDasharray="3 3" />
            {fields.map((f) => (
              <circle key={f.key} cx={x(idx)} cy={y(Number(series[idx][f.key] || 0))}
                      r={3.2} fill={f.colour} stroke="var(--cares-line-card)" strokeWidth="1.5" />
            ))}
            <Tip x={x(idx)} y={PAD.t} width={width}>
              <div style={{ fontWeight: 700 }}>{series[idx].snapshot_date}</div>
              {fields.map((f) => (
                <div key={f.key} style={{ display: "flex", justifyContent: "space-between",
                                          gap: 8 }}>
                  <span style={{ color: f.colour }}>{f.label}</span>
                  <span>{series[idx][f.key]}</span>
                </div>
              ))}
            </Tip>
          </>
        )}

        {[0, Math.floor(series.length / 2), series.length - 1].map((i) => (
          <text key={i} x={x(i)} y={H - 8}
                textAnchor={i === 0 ? "start" : i === series.length - 1 ? "end" : "middle"}
                style={{ fontSize: 9, fill: "var(--cares-surface-raised)" }}>
            {String(series[i]?.snapshot_date || "").slice(5)}
          </text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 10.5,
                    color: "var(--cares-text-label)", marginTop: 4 }}>
        {fields.map((f) => (
          <span key={f.key}>
            <span style={{ display: "inline-block", width: 12, height: 2.5,
                           background: f.colour, marginRight: 5, verticalAlign: "middle" }} />
            {f.label}
          </span>
        ))}
      </div>
    </div>
  );
}
